import { useCallback, useEffect, useState } from 'react';
import {
  getJobsFromAdvancedQuery,
  IConsultant,
  IJob,
  IJobSearchFilterDTO,
  IVendorEmailPayload,
  JobAssignmentStatusFilter,
  vendorBroadcast,
  fetchConsultants,
  IConsultantSearchFilterDTO,
  IUserDTO,
  updateConsumedBroadcastQuota,
} from 'wisestep-api';
import { WsDrawer, VendorEmailForm, JobSelection } from 'wisestep-base';

import { useSKUModalStore } from '../../components/skuModal/SKUModalStore';
import { useUserContext } from '../../context';
import { useWorkspaceContext } from '../../context/WorkspaceContext';
import { careerPageSettingStore } from '../../utils/careerPageSettingsStore';
import { failed, success } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';
import socialMediaShareUrl from '../../utils/shareUrl';
import ConsultantSelection, {
  IConsultantSearchFilter,
} from '../ConsultantSelection/ConsultantSelection';

export interface vendorEmailObject {
  id: string[];
  name: string[];
}

export interface IVendorEmailProps {
  vendorsInLists: vendorEmailObject;
  open: boolean;
  jobsOnly?: boolean;
  consultantsOnly?: boolean;
  handleClose: () => void;
  pageRefersh?: () => void;
  selectedJobs?: IJob[];
  selectedConsultants?: IConsultant[];
  size?: number;
}

interface IApiData {
  jobs: IJob[];
  consultants: IConsultant[];
}

interface IVendorEmailData {
  selectedJobs: IJob[];
  selectedConsultants: IConsultant[];
  totalJobsCount: number;
  totalConsultantsCount: number;
}

const VendorEmail = ({
  vendorsInLists,
  open,
  jobsOnly = false,
  consultantsOnly = false,
  handleClose,
  selectedConsultants,
  selectedJobs,
  pageRefersh,
  size,
}: IVendorEmailProps) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [disable, setDisable] = useState(false);
  const [apiData, setApiData] = useState<IApiData>({
    jobs: [],
    consultants: [],
  });

  const [vendorEmailData, setVendorEmailData] = useState<IVendorEmailData>({
    selectedJobs: [],
    selectedConsultants: [],
    totalJobsCount: 0,
    totalConsultantsCount: 0,
  });
  const workspace = useWorkspaceContext();

  const user = useUserContext().userDTO as IUserDTO;

  const careerPageSettings = careerPageSettingStore((state) => state.careerPageSettings);

  const getJobLink = (job: IJob) => socialMediaShareUrl(job, careerPageSettings);

  const [openJobSelection, setOpenJobSelection] = useState<boolean>(jobsOnly);

  const [openConsultantSelection, setOpenConsultantSelection] = useState<boolean>(consultantsOnly);
  const setModal = useSKUModalStore((state) => state.setModal);

  const handleDrawerButton = () => {
    if (openJobSelection || openConsultantSelection) {
      setOpenJobSelection(false);
      setOpenConsultantSelection(false);
    } else {
      handleClose();
    }
  };

  const broadcast = async (data) => {
    setDisable(true);
    const broadcastData: IVendorEmailPayload = {
      page: 0,
      size: size,
      sort: ['createdOn,desc'],
      vendorsInLists: vendorsInLists.id,
      emailDTO: {
        to: [],
        body: `<div style="box-shadow: 0 4px 8px 0 rgba(0,0,0,0.2);transition: 0.3s;">VENDOR_INFORMATION_LINK<br>' ${data.body}
          '<div style="text-align: center;"><hr><p>Sent by ' ${user.email}
          ' from ' ${workspace.name}
          '</p><hr><div></div>`,
        consultantIds: vendorEmailData.selectedConsultants.map((consultant) => consultant.id || ''),
        consultants: vendorEmailData.selectedConsultants,
        jobIds: vendorEmailData.selectedJobs.map((job) => job.id || ''),
        jobs: vendorEmailData.selectedJobs,
        subject: data.subject,
      },
    };
    try {
      await updateConsumedBroadcastQuota(size ?? 0);
      await vendorBroadcast(broadcastData);
      setNotification(success('Emails sent successfully!'));
      pageRefersh && pageRefersh();
      handleClose();
    } catch (err: any) {
      if (err?.response?.status == 402) {
        err.response.data && setModal({ object: err.response.data, type: 'count' });
      }
      setNotification(failed(err.message));
      handleClose();
    }
  };

  const handleJobParametersChange = (query: string, page: number) => getJobs(query, page);

  const handleConsultantParametersChange = (query: string, page: number) =>
    getConsultants(query, page);

  const handleJobSearch = (data) => getJobs(data.jobSearchQuery, 0, data.numOfResults);

  const handleConsultantSearch = (data: IConsultantSearchFilter) =>
    getConsultants(data.consultantSearchQuery, data.locationSearchQuery);

  const getSelectedJobs = (selectedJobCodes: string[]) => {
    selectedJobCodes.map((jobCode) => {
      apiData.jobs.map((job) => {
        if (job.jobCode === jobCode)
          setVendorEmailData((prev) => ({ ...prev, selectedJobs: [...prev.selectedJobs, job] }));
      });
    });
    setOpenJobSelection(false);
  };

  const getSelectedConsultants = (selectedConsultantIds: string[]) => {
    selectedConsultantIds.map((id) => {
      apiData.consultants.map((consultant) => {
        if (consultant.id === id)
          setVendorEmailData((prev) => ({
            ...prev,
            selectedConsultants: [...prev.selectedConsultants, consultant],
          }));
      });
    });
    setOpenConsultantSelection(false);
  };

  const getJobs = useCallback(
    async (query = '', page = 0, size = 5) => {
      const jobSearchFilter: IJobSearchFilterDTO = {
        datesUnionBy: '',
        jobAssignmentStatusFilter: JobAssignmentStatusFilter.Everything,
        jobStatus: 'All',
        query: query,
        page: page,
        searchAcross: ['MY_TEAMS', 'MY_PERSONAL_DATA'],
        size: size,
        sort: ['postedOn,desc'],
      };
      try {
        const response = await getJobsFromAdvancedQuery(jobSearchFilter);
        setApiData((prev) => ({ ...prev, jobs: response.data }));
        setVendorEmailData((prev) => ({
          ...prev,
          totalJobsCount: parseInt(response.headers['x-total-count']),
        }));
      } catch (err: any) {
        setNotification(failed(err.message));
      }
    },
    [setNotification]
  );

  const getConsultants = useCallback(
    async (query = '', locations = [], page = 0, size = 5) => {
      const consultantSearchFilterDTO: IConsultantSearchFilterDTO = {
        locations: locations,
        page: page,
        query: query,
        size: size,
        sort: ['createdOn,desc'],
      };
      try {
        const response = await fetchConsultants(consultantSearchFilterDTO);
        setApiData((prev) => ({ ...prev, consultants: response.data }));
        setVendorEmailData((prev) => ({
          ...prev,
          totalConsultantsCount: parseInt(response.headers['x-total-count']),
        }));
      } catch (err: any) {
        setNotification(failed(err.message));
      }
    },
    [setNotification]
  );

  useEffect(() => {
    !consultantsOnly && getJobs();
    !jobsOnly && getConsultants();
  }, [jobsOnly, consultantsOnly, getJobs, getConsultants]);

  return (
    <WsDrawer
      iconType={openJobSelection || openConsultantSelection ? 'arrow' : 'cross'}
      title={
        openJobSelection
          ? 'Select Jobs'
          : openConsultantSelection
          ? 'Select Consultants'
          : 'Broadcast To Vendors'
      }
      open={open}
      onClose={handleDrawerButton}
    >
      {openJobSelection && (
        <JobSelection
          jobs={apiData.jobs}
          totalCount={vendorEmailData.totalJobsCount > 25 ? 25 : vendorEmailData.totalJobsCount}
          handleChangeParameters={handleJobParametersChange}
          handleJobSearch={handleJobSearch}
          getSelectedJobs={getSelectedJobs}
          multiple={true}
          handleClose={() => {
            handleClose();
            setOpenJobSelection(false);
          }}
        />
      )}
      {openConsultantSelection && (
        <ConsultantSelection
          consultants={apiData.consultants}
          totalCount={
            vendorEmailData.totalConsultantsCount > 25 ? 25 : vendorEmailData.totalConsultantsCount
          }
          handleChangeParameters={handleConsultantParametersChange}
          getSelectedConsultants={getSelectedConsultants}
          handleConsultantSearch={handleConsultantSearch}
          closeConsultantSelection={() => {
            handleClose();
            setOpenConsultantSelection(false);
          }}
        />
      )}
      {!openJobSelection && !openConsultantSelection && (
        <VendorEmailForm
          vendorList={vendorsInLists.name}
          numOfJobs={
            selectedJobs && selectedJobs?.length > 0
              ? selectedJobs.length
              : vendorEmailData.selectedJobs.length
          }
          numOfConsultants={
            selectedConsultants && selectedConsultants.length > 0
              ? selectedConsultants.length
              : vendorEmailData.selectedConsultants.length
          }
          jobsOnly={selectedJobs && selectedJobs?.length > 0 ? true : jobsOnly}
          consultantsOnly={
            selectedConsultants && selectedConsultants.length > 0 ? true : consultantsOnly
          }
          openJobs={() => setOpenJobSelection(true)}
          openConsultants={() => setOpenConsultantSelection(true)}
          submitAction={broadcast}
          handleClose={handleClose}
          selectedJobs={
            selectedJobs && selectedJobs?.length > 0 ? selectedJobs : vendorEmailData.selectedJobs
          }
          selectedConsultents={
            selectedConsultants && selectedConsultants.length > 0
              ? selectedConsultants
              : vendorEmailData.selectedConsultants
          }
          workspaceContext={workspace}
          userContext={user}
          linkCreation={getJobLink}
          disabled={disable}
        />
      )}
    </WsDrawer>
  );
};

export default VendorEmail;
